import { defineStore } from 'pinia';
import axios from 'axios';

import type { AttendanceCheckInResponse, AttendanceMeResponse } from '@krafta/shared-types';

import { useAuthStore } from '@app/stores/auth';
import {
  createOwnedBlockerNotifications,
  useAppNotifications,
  type AppNotificationAction
} from '@shared/notifications';
import { attendanceApi } from './attendanceApi';

export type AttendanceLockStatus = 'idle' | 'loading' | 'ready' | 'error';

type AttendanceLockState = {
  employeeUserId: string | null;
  routeExempt: boolean;
  status: AttendanceLockStatus;
  required: boolean;
  exempt: boolean;
  active: boolean;
  locked: boolean;
  expired: boolean;
  latestEvent: AttendanceMeResponse['latestEvent'];
  activeShift: AttendanceMeResponse['activeShift'];
  serverTime: string | null;
  maxShiftWindowMinutes: number | null;
  error: string | null;
};

const ATTENDANCE_SYNC_KEY = 'krafta.attendance.updatedAt';
const ATTENDANCE_BLOCKER_KEY = 'attendance:shift-lock';

let refreshPromise: Promise<void> | null = null;
let syncInitialized = false;
let channel: BroadcastChannel | null = null;
const attendanceBlockers = createOwnedBlockerNotifications(
  useAppNotifications()
);

const clearAttendanceBlocker = () => {
  attendanceBlockers.clear('shift-lock');
};

const syncAttendanceBlocker = (
  title: string,
  message: string,
  action?: AppNotificationAction
) => {
  attendanceBlockers.show('shift-lock', {
    key: ATTENDANCE_BLOCKER_KEY,
    title,
    message,
    action
  });
};

export const useAttendanceLockStore = defineStore('attendanceLock', {
  state: (): AttendanceLockState => ({
    employeeUserId: null,
    routeExempt: false,
    status: 'idle',
    required: false,
    exempt: false,
    active: false,
    locked: false,
    expired: false,
    latestEvent: null,
    activeShift: null,
    serverTime: null,
    maxShiftWindowMinutes: null,
    error: null
  }),

  getters: {
    isLocked(): boolean {
      const auth = useAuthStore();
      if (!auth.isAuthenticated) {
        return false;
      }
      if (this.status !== 'ready') {
        return true;
      }
      if (this.exempt) {
        return false;
      }

      return this.locked;
    },

    isLoadingForCurrentUser(): boolean {
      const auth = useAuthStore();
      return auth.isAuthenticated && !this.exempt && this.status === 'loading';
    },

    isErrorForCurrentUser(): boolean {
      const auth = useAuthStore();
      return auth.isAuthenticated && !this.exempt && this.status === 'error';
    }
  },

  actions: {
    setRouteExempt(exempt: boolean) { this.routeExempt = exempt; this.syncPrompt(); },
    syncPrompt() {
      if (this.routeExempt || this.exempt || !this.locked) { clearAttendanceBlocker(); return; }
      if (this.status === 'error') {
        syncAttendanceBlocker('Не удалось проверить смену', this.error ?? 'Не удалось проверить статус смены', {
          label: 'Проверить снова',
          run: async () => {
            await this.refresh({ force: true });
            if (this.status === 'error') throw new Error(this.error ?? 'Не удалось проверить статус смены');
          }
        });
      } else if (this.status === 'ready') {
        syncAttendanceBlocker(
          this.expired ? 'Смена истекла' : 'Сначала откройте смену',
          this.expired
            ? 'Отсканируйте код терминала ещё раз, чтобы продолжить работу.'
            : 'Отсканируйте код терминала, чтобы открыть рабочие разделы.'
        );
      }
    },
    reset() {
      clearAttendanceBlocker();
      this.status = 'idle'; this.routeExempt = false;
      this.employeeUserId = null;
      this.required = false;
      this.exempt = false;
      this.active = false;
      this.locked = false;
      this.expired = false;
      this.latestEvent = null;
      this.activeShift = null;
      this.serverTime = null;
      this.maxShiftWindowMinutes = null;
      this.error = null;
    },

    applyServerState(payload: AttendanceMeResponse, employeeUserId: string) {
      this.employeeUserId = employeeUserId;
      this.status = 'ready';
      this.required = payload.required;
      this.exempt = payload.exempt;
      this.active = payload.active;
      this.locked = payload.locked;
      this.expired = payload.expired;
      this.latestEvent = payload.latestEvent;
      this.activeShift = payload.activeShift;
      this.serverTime = payload.serverTime;
      this.maxShiftWindowMinutes = payload.maxShiftWindowMinutes;
      this.error = null;

      this.syncPrompt();
    },

    async ensureReady() {
      const auth = useAuthStore();
      const employeeUserId = auth.user?.id ?? null;
      if (this.status === 'ready' && this.employeeUserId === employeeUserId) {
        return;
      }

      await this.refresh();
    },

    async refresh(options: { force?: boolean } = {}) {
      const auth = useAuthStore();
      if (!auth.isAuthenticated) {
        this.reset();
        return;
      }

      const employeeUserId = auth.user?.id;
      if (!employeeUserId) {
        this.reset();
        return;
      }

      if (refreshPromise && !options.force) {
        await refreshPromise;
        return;
      }

      const hasResolvedStateForEmployee = this.status === 'ready' && this.employeeUserId === employeeUserId;
      if (!hasResolvedStateForEmployee) {
        this.status = 'loading';
        this.required = true;
        this.exempt = false;
        this.locked = true;
      }
      this.error = null;

      refreshPromise = attendanceApi
        .getMe()
        .then((payload) => {
          this.applyServerState(payload, employeeUserId);
        })
        .catch((error) => {
          this.status = 'error';
          this.required = true;
          this.exempt = false;
          this.active = false;
          this.locked = true;
          this.error = resolveAttendanceStatusError(error);
          this.syncPrompt();
        })
        .finally(() => {
          refreshPromise = null;
        });

      await refreshPromise;
    },

    async applyCheckInResult(result: AttendanceCheckInResponse) {
      this.active = result.active;
      this.locked = result.locked;
      this.expired = false;
      this.latestEvent = result.event;
      if (!result.locked) {
        clearAttendanceBlocker();
      }
      await this.refresh({ force: true });
      this.notifyChanged();
    },

    notifyChanged() {
      const payload = String(Date.now());
      channel?.postMessage({ type: 'attendance-updated', payload });
      window.localStorage.setItem(ATTENDANCE_SYNC_KEY, payload);
    },

    initSync() {
      if (syncInitialized || typeof window === 'undefined') {
        return;
      }

      syncInitialized = true;
      if ('BroadcastChannel' in window) {
        channel = new BroadcastChannel('krafta-attendance');
        channel.onmessage = (event) => {
          if (event.data?.type === 'attendance-updated') {
            void this.refresh({ force: true });
          }
        };
      }

      window.addEventListener('storage', (event) => {
        if (event.key === ATTENDANCE_SYNC_KEY) {
          void this.refresh({ force: true });
        }
      });

      window.addEventListener('focus', () => {
        void this.refresh({ force: true });
      });
    }
  }
});

function resolveAttendanceStatusError(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
  }

  return 'Не удалось проверить статус смены';
}
